import { ObservableMap } from '@stencil/store';
import {
    get,
    getEnvironment,
    startIdleTimeout,
} from '@uipath/portal-shell-util';
import {
    InMemoryWebStorage,
    Log,
    User,
    UserManager,
    UserManagerSettings,
    WebStorageStateStore,
} from 'oidc-client-ts';

import { ISessionStoreState } from '../../models/sessionStoreState';
import { accountAndTenantsStore } from '../accountAndTenants/accountAndTenants';
import { AppInsightsInstanceResolver } from '../appInsights/appInsightsInstanceResolver';
import {
    getEnvConfigField,
    isMsi,
} from '../EnvironmentUtil';
import GlobalEventEmitter from '../events/globalEventEmitters';
import { Features } from '../featureFlags/featureFlagConstants';
import { getFeatureFlag } from '../featureFlags/featureFlags';
import {
    isFalsyOrWhitespace,
    sanitizeToken,
} from '../helper';
import Logger from '../logger/logger';
import { getOrganizationStandardUrl } from '../orgStandardUrlHelper';
import {
    getAccountName,
    getSpaceName,
} from '../urlHelper';
import { FirstPartyClients } from './firstPartyClients';
import OidcLogger from './oidcLogger';

export default class Session {
    private store: ObservableMap<ISessionStoreState>;
    private userManager: UserManager;
    private idleTimerStopCallback: null | (() => void) = null;

    constructor(sessionStore: ObservableMap<ISessionStoreState>) {
        this.store = sessionStore;
    }

    redirectToError(errorCode: number) {
        window.location.href = `/portal_/genericerror?errorCode=${errorCode}`;
    }

    async signInSilent(enableLocal: boolean) {
        try {
            if (this.store.get('isSignInInitialized')) {
                return this.store.get('isSignInSuccessful');
            }

            Log.setLevel(Log.DEBUG);
            Log.setLogger(new OidcLogger());

            Logger.debug('session', 'signInSilent');
            const userManagerInitialized = this.initializeUserManager(enableLocal);
            if (!userManagerInitialized) {
                throw new Error('UserManager missing');
            }

            this.store.set('isSignInInitialized', true);
            Logger.debug('session', 'calling signInSilent on userManager');
            const returnedUser: User | null = await userManagerInitialized.signinSilent();

            if (!returnedUser) {
                throw new Error('Returned user is null');
            }

            Logger.debug('session', 'setAccessToken from signInSilent');
            this.setAccessToken(returnedUser);

            this.enforceCheckSession();

            this.store.set('isSignInSuccessful', true);
            return true;
        } catch (error) {
            Logger.error('session', `sign in silent error: ${error.message}`);
            this.store.set('isSignInInitialized', false);
            this.store.set('isSignInSuccessful', false);
            if (error.message.includes('iat is in the future')) {
                this.redirectToError(7779);
            }
            return false;
        }
    }

    enforceIdleTimeout(idleSessionTimeoutPeriod?: number | null) {
        if (this.idleTimerStopCallback) {
            Logger.debug('idle timeout', 'enforceIdleTimeout found existing idleTimeout, returning');
            return;
        }

        const envConfigTimeoutInterval = (getEnvConfigField('IDLE_TIMEOUT_INTERVAL') ?? 15) * 60 * 1000;
        this.idleTimerStopCallback = startIdleTimeout((localStorageTimer: number | null) => {
            const currentTime = Date.now();
            const interval = idleSessionTimeoutPeriod ?? envConfigTimeoutInterval;

            if (interval && localStorageTimer && currentTime >= localStorageTimer + interval) {
                Logger.debug('idle timeout', `localstorage timer + interval: ${localStorageTimer + interval} >= current timer: ${currentTime}`);
                GlobalEventEmitter.emitSessionTimedOut();
            }
        });
    }

    cancelIdleTimeout() {
        Logger.debug('idle timeout', `cancelling idle timeout: ${this.idleTimerStopCallback}`);
        this.idleTimerStopCallback?.();
        this.idleTimerStopCallback = null;
    }

    enforceCheckSession() {
        // Always poll for session validity, even if the user setting is false
        const allowConcurrentSessionsSetting = this.isSingleSessionEnforcementEnabled();
        Logger.info('session', `starting /checksession poll; allowConcurrentSessionSetting&&Flag=${allowConcurrentSessionsSetting}`);
        const checkSessionInterval = +getFeatureFlag<number>(Features.CheckSessionIntervalMs.name);
        const checkSessionInactiveInterval = +getFeatureFlag<number>(Features.CheckSessionInactiveIntervalMs.name) ?? 5 * 30 * 1000;

        // Repurposed the startIdleTimeout function to poll for session validity as well
        // It already tracks the last action time and runs a callback on an interval
        startIdleTimeout((lastActionTime) => {
            const currentTime = Date.now();
            if (!lastActionTime || currentTime <= lastActionTime + checkSessionInactiveInterval) {
                // eslint-disable-next-line spaced-comment
                this.checkSession(); //NOSONAR async call but async functions don't work with internal setInterval
            }
        }, checkSessionInterval ?? 30 * 1000);
    }

    /*
    * Gets the current stored access token with the default token as the backup when the self auth flag isn't on
    * This getAccessToken is intended for use when the token doesn't need to trigger rerender.
    * For internal use where rerendering is required, use the getAccessToken in the sessionStore file instead.
    */
    getAccessToken(defaultToken?: string): string {
        Logger.debug('session', `getAccessToken called with sanitized defaultToken: ${sanitizeToken(defaultToken)} and sanitized accessToken: ${sanitizeToken(this.store.get('accessToken'))}`);
        const fallbackToken = !isFalsyOrWhitespace(defaultToken) ? defaultToken : this.store.get('accessToken');
        const selfAuthFlag = getFeatureFlag(Features.EnablePortalShellSelfAuth.name);
        if (!selfAuthFlag) {
            Logger.info('session', `getAccessToken returning fallback access token: flag=${selfAuthFlag}, userManager=${!!this.userManager}, signInInitialized=${this.store.get('isSignInInitialized')}, defaultToken=${sanitizeToken(defaultToken)}, storeToken=${sanitizeToken(this.store.get('accessToken'))}, selfAuthToken=${sanitizeToken(this.store.get('selfAuthAccessToken'))}`);
            return fallbackToken ?? '';
        }
        const selfAuthAccessToken = this.store.get('selfAuthAccessToken');
        Logger.debug('session', `returning self auth access token, flag=${selfAuthFlag} selfAuthAccessToken=${sanitizeToken(selfAuthAccessToken)}`);
        return selfAuthAccessToken;
    }

    async logout(emitLogoutEvent: boolean = false) {
        try {
            Logger.info([ 'session', 'idle timeout' ], 'signing out');
            if (emitLogoutEvent) {
                GlobalEventEmitter.emitLogout();
                return;
            }

            if (!this.userManager || !this.store.get('isSignInInitialized')) {
                throw new Error(`UserManager missing: ${!this.userManager} or is already logged out`);
            }

            this.store.set('isSignInInitialized', false);
            this.store.set('isSignInSuccessful', false);
            await this.userManager.signoutRedirect();
        } catch (error) {
            Logger.error([ 'session', 'idle timeout' ], `sign out error: ${error.message}`);
        }
    }

    // The event handlers are static with the delegate passed in so that they can be run properly
    private static async onUserLoadedCallback(delegate: Session, user: User, enableLocal: boolean) {
        Logger.info('session', 'user loaded event');
        try {
            delegate.setAccessToken(user);

            const enableFirstPartClientPrefetch = getFeatureFlag(Features.EnableFirstPartyClientPrefetch.name);
            Logger.debug([ 'session', 'prefetch' ], `prefetch flag state ${enableFirstPartClientPrefetch}`);
            if (enableFirstPartClientPrefetch) {
                delegate.registerFirstPartyClients(enableLocal);
            }
        } catch (error) {
            Logger.error('session', `user loaded setAccessToken failed with error: ${error.message}`);
        }
    }

    private static async onLogoutCallback(
        delegate: Session,
        eventName: string,
        singleSessionEnforcement: boolean,
    ) {
        const message = singleSessionEnforcement
            ? `${eventName}, enforcing single session; allowConcurrentSessionSetting&&Flag=${singleSessionEnforcement}`
            : `${eventName}; allowConcurrentSessionSetting&&Flag=${singleSessionEnforcement}`;
        Logger.info('session', message);
        delegate.logout();
    }

    private getCISUrls(env = getEnvironment(), local = false) {
        const organizationStandardUrl = getOrganizationStandardUrl({ shouldAlwaysExcludeAccountName: true });
        const CISAuthority = getEnvConfigField('CIS_HOST_PREFIX', env, local) || `${organizationStandardUrl}/identity${isMsi() ? '' : '_'}`;
        Logger.debug('session', `CISAuthority: ${CISAuthority}`);

        const CISHostConnect = `${CISAuthority}/connect`;
        const PortalOrigin = getEnvConfigField('PORTAL_ORIGIN', env, local) || organizationStandardUrl;
        const CISOpenIdConfiguration = `${CISAuthority}/.well-known/openid-configuration`;
        return {
            CISAuthority,
            CISHostConnect,
            PortalOrigin,
            CISOpenIdConfiguration,
        };
    }

    private getUserManagerAuthConfiguration(env, enableLocal) {
        const {
            CISAuthority, CISHostConnect, PortalOrigin, CISOpenIdConfiguration,
        } = this.getCISUrls(env, enableLocal);

        const clientId = getEnvConfigField('CIS_PKCE_CLIENT_ID', env, enableLocal);

        // Local metadata is separate due to CORs issue accessing openid-configuration endpoint
        const metadata = enableLocal ? {
            metadata: {
                issuer: CISAuthority,
                jwks_uri: `${CISAuthority}/.well-known/openid-configuration/jwks`,
                authorization_endpoint: `${CISHostConnect}/authorize`,
                token_endpoint: `${CISHostConnect}/token`,
                userinfo_endpoint: `${CISHostConnect}/userinfo`,
                end_session_endpoint: `${CISHostConnect}/endsession`,
                check_session_iframe: `${CISHostConnect}/checksession`,
                revocation_endpoint: `${CISHostConnect}/revocation`,
                introspection_endpoint: `${CISHostConnect}/introspect`,
            },
        } : { metadataUrl: CISOpenIdConfiguration };

        const authConfiguration: UserManagerSettings = {
            authority: CISAuthority,
            client_id: clientId,
            post_logout_redirect_uri: `${PortalOrigin}/portal_/sessionexpired`,
            response_type: 'code',
            redirect_uri: `${PortalOrigin}/portal_/api/tokenFactory/callback`,
            silent_redirect_uri: `${PortalOrigin}/portal_/api/tokenFactory/callback`,
            scope: 'openid profile email IdentityServerApi offline_access',
            loadUserInfo: false,
            automaticSilentRenew: true,
            monitorSession: false,
            stopCheckSessionOnError: true,
            userStore: new WebStorageStateStore({ store: new InMemoryWebStorage() }),
            ...metadata,
        };

        return authConfiguration;
    }

    private getMsiUserManagerAuthConfiguration(env, enableLocal) {
        const {
            CISAuthority, PortalOrigin, CISOpenIdConfiguration,
        } = this.getCISUrls(env, enableLocal);

        const clientId = getEnvConfigField('CIS_PKCE_CLIENT_ID', env, enableLocal);
        const authConfiguration: UserManagerSettings = {
            authority: CISAuthority,
            client_id: clientId,
            post_logout_redirect_uri: `${PortalOrigin}/identity/management/sessionexpired`,
            response_type: 'code',
            redirect_uri: `${PortalOrigin}/identity/management/pkceTokenFactory.html`,
            silent_redirect_uri: `${PortalOrigin}/identity/management/pkceTokenFactory.html`,
            scope: 'openid profile email IdentityServerApi offline_access',
            loadUserInfo: false,
            automaticSilentRenew: true,
            stopCheckSessionOnError: true,
            monitorSession: false,
            metadataUrl: CISOpenIdConfiguration,
        };

        return authConfiguration;
    }

    private initializeUserManager(enableLocal: boolean) {
        if (this.userManager) {
            return this.userManager;
        }

        try {
            const env = getEnvironment();
            Logger.debug('session', `Environment is ${env}`);
            const singleSessionEnforcement = this.isSingleSessionEnforcementEnabled();

            const authConfig = isMsi()
                ? this.getMsiUserManagerAuthConfiguration(env, enableLocal)
                : this.getUserManagerAuthConfiguration(env, enableLocal);

            const manager = new UserManager(authConfig);
            this.userManager = manager;

            // These are done using => because using functions doesn't pass in the delegate properly
            this.userManager.events.addUserLoaded(
                (user: User) => Session.onUserLoadedCallback(this, user, enableLocal),
            );
            this.userManager.events.addUserSignedOut(
                () => Session.onLogoutCallback(this, 'user signed out event', singleSessionEnforcement),
            );
            this.userManager.events.addAccessTokenExpired(
                () => Session.onLogoutCallback(this, 'access token expired event', singleSessionEnforcement),
            );

            this.userManager.events.addAccessTokenExpiring(function() {
                Logger.info('session', 'access token expiring event');
            });
            this.userManager.events.addSilentRenewError(function() {
                Logger.info('session', 'silent renew error event');
            });
            this.userManager.events.addUserSessionChanged(function() {
                Logger.info('session', 'user session changed event');
            });
            this.userManager.events.addUserSignedIn(function() {
                Logger.info('session', 'user signed in event');
            });
            this.userManager.events.addUserUnloaded(function() {
                Logger.info('session', 'user unloaded event');
            });

            Logger.debug('session', `UserManager initialized successfully: ${!!this.userManager}`);
            return this.userManager;
        } catch (error) {
            Logger.error('session', `failed to initialize UserManager: ${error.message}`);
            return null;
        }
    }

    private isSingleSessionEnforcementEnabled() {
        const allowConcurrentSessionsSetting = accountAndTenantsStore.get('organization')?.allowConcurrentSessions;
        return allowConcurrentSessionsSetting?.toLowerCase() === 'false';
    }

    private async checkSession() {
        const { CISAuthority } = this.getCISUrls();
        await get(
            `${CISAuthority}/api/checksession`,
            {
                token: '',
                credentials: 'same-origin',
            },
            (e) => {
                Logger.error('session', `check session error: ${e.message}`);
            },
            (res) => {
                Logger.debug('session', 'check session response: ' + res.status);
                if (res.status === 401) {
                    Logger.info('session', 'check session failed with 401, logging out');
                    this.logout();
                }
            },
        );
    }

    private setAccessToken(user: User) {
        if (!user) {
            throw new Error('User not returned after sign in or token renewal');
        }
        if (!user.access_token) {
            throw new Error('User missing access token after sign in or token renewal');
        }

        // Update session id of the user in app insights
        if (user.profile?.sid) {
            AppInsightsInstanceResolver.SetPlatformInfo({ sessionId: user.profile.sid });
        }

        Logger.debug('session', 'silent login or renewal successful');
        this.store.set('selfAuthAccessToken', user.access_token);
    }

    private registerFirstPartyClients(enableLocal: boolean) {
        const env = getEnvironment();
        const service = getSpaceName();
        const accountName = getAccountName();

        const {
            CISAuthority, PortalOrigin,
        } = this.getCISUrls(env, enableLocal);

        const firstPartyClientRegistrations = Object
            .entries(FirstPartyClients)
            .filter(([ k, _ ]) => k !== service) // TODO: limit to services available for user
            .map(async ([ client, config ]) => {
                const {
                    clientId, scopes, storage,
                } = config;

                const fpsUserManager = new UserManager({
                    client_id: clientId,
                    authority: CISAuthority,
                    redirect_uri: `${PortalOrigin}/portal_/api/tokenFactory/callback`,
                    response_type: 'code',
                    response_mode: 'query',
                    scope: scopes.join(' '),
                    stopCheckSessionOnError: true,
                    monitorSession: false,
                    automaticSilentRenew: false,
                    userStore: new WebStorageStateStore(
                        { store: storage === 'default' ? window.sessionStorage : new InMemoryWebStorage() }),
                    acr_values: `tenantName:${accountName}`,
                });

                // conditions to skip pre-fetch
                if (await fpsUserManager.getUser()) {
                    return;
                }

                if (storage !== 'default') {
                    const key = storage.key.replace('$accountName', accountName ?? '');

                    if (storage.location.getItem(key)) {
                        return;
                    }
                }

                Logger.info([ 'session', 'prefetch' ], `pre-fetching auth storage for ${client}`);

                return fpsUserManager.signinSilent()
                    .then((u: User) => {
                        Logger.info([ 'session', 'prefetch' ], `pre-fetch successful for ${client}`);

                        // case where schema and key are default
                        if (!u || storage === 'default') {
                            return;
                        }

                        // case where accountName is part of key
                        const key = storage.key.replace('$accountName', accountName ?? '');

                        // case where schema is default, but key is custom
                        if (storage.schema === 'default') {
                            storage.location.setItem(key, u.toStorageString());
                            return;
                        }

                        // use split and join to cover all occurrences
                        const auth = JSON.stringify(storage.schema)
                            .replace('$user.id_token', u.id_token ?? '')
                            .replace('$user.access_token', u.access_token)
                            .replace('$user.session_state', u.session_state ?? '')
                            .replace('$user.scope', u.scope ?? '')
                            .replace('$user.state', u.state as string ?? '')
                            .replace('$user.expires_at', JSON.stringify(u.expires_at))
                            .replace('$user.profile', JSON.stringify(u.profile))
                            .replace('"{', '{')
                            .replace('}"', '}');

                        // case where schema is custom, and key is custom
                        storage.location.setItem(key, auth);

                    })
                    .catch(error =>
                        Logger.warn([ 'session', 'prefetch' ], `pre-fetch failed for ${client} with message ${error.message}`),
                    );
            });

        Promise.all(firstPartyClientRegistrations);
    }
}
